import * as React from 'react';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '@bliks-io/ui';
import { UserSelect, type UserSelectProps } from './UserSelect';

export interface FormFieldUserSelectProps extends UserSelectProps {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;
}

/**
 * UserSelect with Field wrapper (label, description, error)
 * Can be used standalone or wrapped by FormUserSelect for RHF integration
 *
 * @example
 * ```tsx
 * const [userId, setUserId] = useState<string>();
 *
 * <FormFieldUserSelect
 *   label="Assign to User"
 *   value={userId}
 *   onChange={setUserId}
 *   required
 *   error={errors.userId?.message}
 * />
 * ```
 */
export const FormFieldUserSelect = React.forwardRef<HTMLButtonElement, FormFieldUserSelectProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      orientation = 'vertical',
      fieldClassName,
      name,
      id,
      ...selectProps
    },
    ref
  ) => {
    const fieldId = id || name;
    const hasError = !!error;

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          <UserSelect {...selectProps} id={fieldId} name={name} ref={ref} />
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldUserSelect.displayName = 'FormFieldUserSelect';
